/* eslint-disable camelcase */
import { QueryFunctionContext } from '@tanstack/react-query';
import { api } from '../services/api';

/**
 * Limite de notas carregadas no drill-down de uma empresa (modal de detalhe).
 * Compartilhado entre o hook (envia no `limit` do request) e o componente
 * (decide quando exibir o banner de truncamento).
 */
export const EMPRESA_DRILL_DOWN_LIMIT = 1000;

export interface IFiscalContabilItem {
  id: number;
  codi_emp: string;
  tipo: number;
  codigo_nota: number;
  documento?: string | null;
  data_emissao?: string | null;
  data_nota?: string | null;
  valor_contabil: number;
  acumulador?: number | null;
  acumulador_nome?: string | null;
  valor: number;
  ccre_acu: number;
  descricao_credito?: string | null;
  cdeb_acu: number;
  descricao_debito?: string | null;
  historico: number;
  created_at?: string | null;
  updated_at?: string | null;
}

export interface IFiscalContabilListResponse {
  total: number;
  items: IFiscalContabilItem[];
}

export interface IResumoTipo {
  tipo: number;
  tipo_nome: string;
  quantidade: number;
  valor_total: number;
}

export interface IResumoEmpresa {
  codi_emp: string;
  total_entradas: number;
  total_saidas: number;
  total_servicos: number;
  valor_entradas: number;
  valor_saidas: number;
  valor_servicos: number;
}

export interface IResumoDivergencias {
  total: number;
  diferenca_total: number;
  valor_contabil_total: number;
  valor_total: number;
  total_empresas: number;
  por_tipo: {
    entradas: number;
    saidas: number;
    servicos: number;
  };
  // Valor monetario real divergente por tipo (SUM da diferenca abs no SQL).
  // Substitui a estimativa proporcional (qtdDiv/qtdTotal*valor) que era
  // imprecisa em datasets concentrados.
  valor_por_tipo: {
    entradas: number;
    saidas: number;
    servicos: number;
  };
}

export interface IResumoAcumulador {
  acumulador: number | null;
  acumulador_nome: string | null;
  quantidade: number;
  valor_total: number;
}

export interface IEmpresasPorTipo {
  total: number;
  entradas: number;
  saidas: number;
  servicos: number;
}

export interface IEvolucaoMensal {
  mes: number;
  mes_nome: string;
  total: number;
  entradas: number;
  saidas: number;
  servicos: number;
  valor_total: number;
  valor_entradas: number;
  valor_saidas: number;
  valor_servicos: number;
  divergencias: number;
  valor_divergencias: number;
}

export interface IEmpresaFiscal {
  codi_emp: string;
  razao_social?: string | null;
  cnpj?: string | null;
  cidade?: string | null;
  uf?: string | null;
}

// Empresa pré-agregada via GROUP BY no backend — cada item da lista de cards
// vem com todos os totals e divergências prontos.
export interface IEmpresaAgregada {
  codi_emp: string;
  razao_social: string | null;
  cnpj: string | null;
  cidade: string | null;
  uf: string | null;
  total_registros: number;
  total_entradas: number;
  total_saidas: number;
  total_servicos: number;
  valor_total: number;
  valor_entradas: number;
  valor_saidas: number;
  valor_servicos: number;
  divergencias: number;
  valor_divergencias: number;
}

export interface IEmpresaAgregadaListResponse {
  total: number;
  items: IEmpresaAgregada[];
}

export interface IFiscalContabilFilters {
  codi_emp?: string;
  tipo?: number | null;
  acumulador?: number | null;
  data_nota_inicio?: string;
  data_nota_fim?: string;
  apenas_divergentes?: boolean;
  page?: number;
  size?: number;
}

// Filtros aceitos pelos endpoints de resumo (sem paginacao). Exportado pra
// callers poderem tipar objetos de filtros sem replicar o Pick<>.
export type IResumoFilters = Pick<
  IFiscalContabilFilters,
  'codi_emp' | 'tipo' | 'acumulador' | 'data_nota_inicio' | 'data_nota_fim' | 'apenas_divergentes'
>;

interface ApiResponse<T> {
  data: T;
  meta?: Record<string, unknown>;
  message?: string;
}

export const useFiscalContabil = () => {
  const getFiscalContabilList = async (
    key: QueryFunctionContext,
  ): Promise<IFiscalContabilListResponse> => {
    const filters = (key.queryKey?.[1] ?? {}) as IFiscalContabilFilters;

    const { page = 1, size = 50, ...rest } = filters;
    const skip = (page - 1) * size;

    const params = {
      ...rest,
      skip,
      limit: size,
    };

    const res = await api.get<ApiResponse<IFiscalContabilListResponse>>(
      '/api/v1/fiscal-contabil/',
      { params },
    );

    return res.data.data;
  };

  const getFiscalContabilListInfinite = async (
    filters: Omit<IFiscalContabilFilters, 'page' | 'size'>,
    page: number,
    size: number,
  ): Promise<IFiscalContabilListResponse> => {
    const skip = (page - 1) * size;

    const params = {
      ...filters,
      skip,
      limit: size,
    };

    const res = await api.get<ApiResponse<IFiscalContabilListResponse>>(
      '/api/v1/fiscal-contabil/',
      { params },
    );

    return res.data.data;
  };

  const getResumoPorTipo = async (key: QueryFunctionContext): Promise<IResumoTipo[]> => {
    const filters = (key.queryKey?.[1] ?? {}) as IResumoFilters;
    const res = await api.get<ApiResponse<IResumoTipo[]>>('/api/v1/fiscal-contabil/resumo/tipo/', {
      params: filters,
    });
    return res.data.data;
  };

  const getResumoDivergencias = async (key: QueryFunctionContext): Promise<IResumoDivergencias> => {
    const filters = (key.queryKey?.[1] ?? {}) as IResumoFilters;
    const res = await api.get<ApiResponse<IResumoDivergencias>>(
      '/api/v1/fiscal-contabil/resumo/divergencias/',
      { params: filters },
    );
    return res.data.data;
  };

  const getResumoAcumuladores = async (key: QueryFunctionContext): Promise<IResumoAcumulador[]> => {
    const filters = (key.queryKey?.[1] ?? {}) as IResumoFilters;
    const res = await api.get<ApiResponse<IResumoAcumulador[]>>(
      '/api/v1/fiscal-contabil/resumo/acumuladores/',
      { params: filters },
    );
    return res.data.data;
  };

  const getEmpresasPorTipo = async (key: QueryFunctionContext): Promise<IEmpresasPorTipo> => {
    const filters = (key.queryKey?.[1] ?? {}) as IResumoFilters;
    const res = await api.get<ApiResponse<IEmpresasPorTipo>>(
      '/api/v1/fiscal-contabil/resumo/empresas-tipo/',
      { params: filters },
    );
    return res.data.data;
  };

  const getResumoPorEmpresa = async (key: QueryFunctionContext): Promise<IResumoEmpresa> => {
    const params = key.queryKey?.[1] as { codi_emp?: string } | undefined;
    const codi_emp = params?.codi_emp;
    if (!codi_emp) {
      throw new Error('codi_emp é obrigatório em getResumoPorEmpresa');
    }
    const res = await api.get<ApiResponse<IResumoEmpresa>>(
      `/api/v1/fiscal-contabil/resumo/empresa/${codi_emp}/`,
    );
    return res.data.data;
  };

  const getEmpresasFiscais = async (key: QueryFunctionContext): Promise<IEmpresaFiscal[]> => {
    const filters = (key.queryKey?.[1] ?? {}) as IResumoFilters;
    const res = await api.get<ApiResponse<IEmpresaFiscal[]>>('/api/v1/fiscal-contabil/empresas/', {
      params: filters,
    });
    return res.data.data;
  };

  const getEvolucaoMensal = async (key: QueryFunctionContext): Promise<IEvolucaoMensal[]> => {
    // evolucao-mensal nao aceita tipo nem apenas_divergentes (ver controller).
    const filters = (key.queryKey?.[1] ?? {}) as {
      ano?: number;
      codi_emp?: string;
      acumulador?: number | null;
    };
    const res = await api.get<ApiResponse<IEvolucaoMensal[]>>(
      '/api/v1/fiscal-contabil/evolucao-mensal/',
      { params: filters },
    );
    return res.data.data;
  };

  // Lista de empresas pre-agregadas (1 empresa = 1 card pronto). Substitui o
  // padrao de paginar notas individuais e agrupar client-side, que disparava
  // dezenas de requests ao rolar pra preencher cards.
  const getEmpresasAgregadasInfinite = async (
    filters: Omit<IFiscalContabilFilters, 'page' | 'size'>,
    page: number,
    size: number,
  ): Promise<IEmpresaAgregadaListResponse> => {
    const skip = (page - 1) * size;
    const params = { ...filters, skip, limit: size };
    const res = await api.get<ApiResponse<IEmpresaAgregadaListResponse>>(
      '/api/v1/fiscal-contabil/empresas-agregadas/',
      { params },
    );
    return res.data.data;
  };

  // Drill-down: busca notas individuais de UMA empresa quando o user abre
  // o modal. Endpoint ja existe (`GET /empresa/{codi_emp}/`) e suporta paginacao.
  const getFiscalContabilByEmpresa = async (
    key: QueryFunctionContext,
  ): Promise<IFiscalContabilItem[]> => {
    const params = key.queryKey?.[1] as
      | { codi_emp?: string; skip?: number; limit?: number }
      | undefined;
    const codiEmp = params?.codi_emp;
    if (!codiEmp) {
      throw new Error('codi_emp é obrigatório em getFiscalContabilByEmpresa');
    }
    const res = await api.get<ApiResponse<IFiscalContabilItem[]>>(
      `/api/v1/fiscal-contabil/empresa/${codiEmp}/`,
      {
        params: { skip: params?.skip ?? 0, limit: params?.limit ?? EMPRESA_DRILL_DOWN_LIMIT },
      },
    );
    return res.data.data;
  };

  return {
    getFiscalContabilList,
    getFiscalContabilListInfinite,
    getEmpresasAgregadasInfinite,
    getFiscalContabilByEmpresa,
    getResumoPorTipo,
    getResumoPorEmpresa,
    getEmpresasFiscais,
    getResumoDivergencias,
    getResumoAcumuladores,
    getEmpresasPorTipo,
    getEvolucaoMensal,
  };
};
